import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, AlertOctagon, Briefcase, Library, User } from 'lucide-react';

/**
 * Mobile-first bottom navigation bar.
 * Fixed to the bottom of the viewport on mobile; sticks to the bottom of
 * the page container at desktop widths.
 */
const ITEMS = [
  { key: 'home',    label: 'Home',    icon: Home,         path: '/',             match: ['/'] },
  { key: 'issues',  label: 'Issues',  icon: AlertOctagon, path: '/report-issue', match: ['/report-issue'] },
  { key: 'cases',   label: 'Cases',   icon: Briefcase,    path: '/case-locker',  match: ['/case-locker'] },
  { key: 'library', label: 'Library', icon: Library,      path: '/library',      match: ['/library'] },
  { key: 'profile', label: 'Profile', icon: User,         path: '/profile',      match: ['/profile'] },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  function isActive(item) {
    if (item.path === '/') return pathname === '/';
    return item.match.some((p) => pathname === p || pathname.startsWith(p + '/'));
  }

  return (
    <nav className="asnav-root" data-testid="bottom-nav" aria-label="Primary">
      <style>{styles}</style>
      <div className="asnav-inner">
        {ITEMS.map((it) => {
          const active = isActive(it);
          const Icon = it.icon;
          return (
            <button
              key={it.key}
              type="button"
              className={`asnav-item ${active ? 'asnav-item--active' : ''}`}
              onClick={() => navigate(it.path)}
              data-testid={`bottom-nav-${it.key}`}
              aria-current={active ? 'page' : undefined}
              aria-label={it.label}
            >
              <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
              <span className="asnav-label">{it.label}</span>
              {active && <span className="asnav-dot" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

const styles = `
.asnav-root {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 50;
  background: rgba(8, 14, 38, 0.96);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid rgba(250, 204, 21, 0.22);
  padding: 6px 0 calc(6px + env(safe-area-inset-bottom));
}
@media (min-width: 1024px) {
  /* Hide redundant bottom nav once the desktop sidebar is showing */
  .asnav-root { display: none; }
}
.asnav-inner {
  max-width: 540px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  /* Reserve right padding so the floating "Made with Emergent" badge
     (anchored bottom-right by the host platform, ~160px wide) does NOT
     overlap the rightmost nav items (Library / Profile). */
  padding: 0 180px 0 8px;
}
@media (min-width: 600px) {
  .asnav-inner { padding: 0 8px; }
}
.asnav-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 6px 4px 4px;
  background: none;
  border: none;
  color: #71717a;
  cursor: pointer;
  border-radius: 12px;
  transition: color 0.18s, background 0.18s, transform 0.12s;
  min-height: 50px;
}
.asnav-item:hover { color: #e2e8f0; background: rgba(255,255,255,0.04); }
.asnav-item:active { transform: scale(0.96); }
.asnav-item--active { color: #facc15; }
.asnav-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.asnav-dot {
  position: absolute;
  bottom: 1px;
  width: 22px; height: 3px;
  border-radius: 3px;
  background: #facc15;
  box-shadow: 0 0 10px rgba(250,204,21,0.55);
}
`;
